import React, { createContext, ReactNode, useCallback, useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import { NPVRStreamAtom } from '@smart-tv/atoms';
import { StreamParams } from '@smart-tv/components/player/types';
import { StreamType } from '@smart-tv/typings/content';
import { Stream } from '@smart-tv/components/player/types';
import { updateStartoverData } from '@smart-tv/utils/kmm/updateStartoverData';
import { getStreamUrl } from '@smart-tv/utils/kmm/getStreamUrl';
import { closeStream } from '@smart-tv/utils/kmm/closeStream';
import { KMMError } from '@norlys-a-s/norlys-play-kmm';
import disableFastForwardCatchup from '@smart-tv/components/player/player-controls/utils/disable-fastforward-catchup';

import { CmdError, BaseError } from '@smart-tv/contexts/error-context/error-context';

export interface StreamContextProps {
  children: ReactNode | ReactNode[];
}

export interface StreamContextValues {
  handleCloseStream: (streamParams: StreamParams) => Promise<boolean>;
  handleOpenStream: (streamParams: StreamParams) => Promise<Stream | undefined>;
  handleSetStreamType: (streamType: StreamType) => void;
  stream?: Stream;
  streamType?: StreamType;
  fastForwardLimitSeconds: number;
  secondsWatched: number;
  setSecondsWatched: (seconds: number) => void;
  setFastForwardLimitSeconds: (seconds: number) => void;
  streamTitles?: StreamTitles;
  error: BaseError | null;
  setError: React.Dispatch<React.SetStateAction<BaseError | null>>;
}

interface StreamTitles {
  title: string;
  subtitle: string;
}

export const StreamContext = createContext<StreamContextValues>({} as StreamContextValues);

export const StreamConsumer = StreamContext.Consumer;

export const StreamProvider = (props: StreamContextProps): JSX.Element => {
  const { children } = props;
  const [streamType, setStreamType] = useState<StreamType>();
  const [stream, setStream] = useState<Stream>();
  const [streamTitles, setStreamTitles] = useState<StreamTitles>();
  const [fastForwardLimitSeconds, setFastForwardLimitSeconds] = useState(0);
  const [secondsWatched, setSecondsWatched] = useState(0);
  const [error, setError] = useState<BaseError | null>(null);
  const [npvrStream, setNPVRStream] = useAtom(NPVRStreamAtom);

  let refreshSheetTimeout: NodeJS.Timeout | null = null;

  const handleSetStreamType = useCallback((streamType: StreamType) => {
    setStreamType(streamType);
  }, []);

  const scheduleTitleUpdateAtTimeBoundary = (seconds: number, sid: number) => {
    clearScheduledTitleUpdate();
    refreshSheetTimeout = setTimeout(() => {
      updateTitles(sid);
    }, seconds * 1000);
  };

  const clearScheduledTitleUpdate = () => {
    if (refreshSheetTimeout) {
      clearTimeout(refreshSheetTimeout);
      refreshSheetTimeout = null;
    }
  };

  const updateTitles = async (sid: number) => {
    const response = await updateStartoverData(sid);

    const streamTitles: StreamTitles = {
      title: response.title,
      subtitle: response.subtitle
    };

    setStreamTitles(streamTitles);

    if (response.updateStartoverNextInSecs) {
      scheduleTitleUpdateAtTimeBoundary(response.updateStartoverNextInSecs, sid);
    }
  };

  const handleOpenStream = useCallback(
    async (streamParams: StreamParams) => {
      try {
        const stream = await getStreamUrl(streamParams);

        if (stream.updateStartoverNextInSecs && streamParams.sid) {
          scheduleTitleUpdateAtTimeBoundary(stream.updateStartoverNextInSecs, streamParams.sid);
        }
        setNPVRStream((prev) => (prev ? { ...prev, length: stream?.duration ?? 0 } : prev));

        const streamTitles: StreamTitles = {
          title: stream.title,
          subtitle: stream.subtitle
        };
        const secondsWatched = stream.startPoint || 0;
        setStreamTitles(streamTitles);
        setFastForwardLimitSeconds(
          disableFastForwardCatchup(stream.trickmodes) ? Math.round(stream.fastForwardLimit) : 0
        );
        setStream(stream);
        setSecondsWatched(secondsWatched);

        return stream;
      } catch (error) {
        if ((error as BaseError).offline !== undefined) {
          throw error;
        } else if (error instanceof KMMError.ApiError) {
          throw new CmdError(
            parseInt((error as KMMError.ApiError).code.toString(), 10),
            (error as KMMError.ApiError).message
          );
        }
        throw new CmdError(
          (error as CmdError).code,
          error instanceof KMMError ? (error as KMMError).message : (error as CmdError).info,
          (error as CmdError).offline
        );
      }
    },
    [npvrStream]
  );

  const handleCloseStream = useCallback(
    async (streamParams: StreamParams) => {
      setNPVRStream((prev) => (prev ? { ...prev, progress: streamParams.progress } : prev));
      setStreamTitles(undefined);
      setStream(undefined);
      setFastForwardLimitSeconds(0);
      clearScheduledTitleUpdate();

      const response = await closeStream(streamParams);
      return response;
    },
    [npvrStream]
  );

  const contextValue = useMemo(
    () => ({
      stream,
      streamType,
      handleOpenStream,
      handleCloseStream,
      handleSetStreamType,
      fastForwardLimitSeconds,
      setFastForwardLimitSeconds,
      streamTitles,
      error,
      setError,
      secondsWatched,
      setSecondsWatched
    }),
    [
      stream,
      streamType,
      handleOpenStream,
      handleCloseStream,
      handleSetStreamType,
      fastForwardLimitSeconds,
      streamTitles,
      error,
      secondsWatched
    ]
  );

  return <StreamContext.Provider value={contextValue}>{children}</StreamContext.Provider>;
};

export default StreamContext;
